import { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import apiClient from "@/utils/apiClient";

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import MindSpa from "@/pages/MindSpa";
import AICoach from "@/pages/AICoach";
import MoodTracker from "@/pages/MoodTracker";
import SMAHOOCoins from "@/pages/SMAHOOCoins";
import GiftBasketStore from "@/pages/GiftBasketStore";
import LifeSkills from "@/pages/LifeSkills";
import Community from "@/pages/Community";
import OnCallLive from "@/pages/OnCallLive";
import Streaming from "@/pages/Streaming";
import StreamingBasic from "@/pages/StreamingBasic";
import Sessions from "@/pages/Sessions";
import CalendarPage from "@/pages/CalendarPage";
import Profile from "@/pages/Profile";

// Admin Pages
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminContent from "@/pages/admin/AdminContent";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminDigest from "@/pages/admin/AdminDigest";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminAffiliate from "@/pages/admin/AdminAffiliate";
import AdminMall from "@/pages/admin/AdminMall";
import AdminRetreat from "@/pages/admin/AdminRetreat";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminMedia from "@/pages/admin/AdminMedia";

// User-Facing Pages
import Blog from "@/pages/Blog";
import Digest from "@/pages/Digest";
import Affiliate from "@/pages/Affiliate";
import WellnessMall from "@/pages/WellnessMall";
import Retreat from "@/pages/Retreat";
import BookAppointment from "@/pages/BookAppointment";
import Pricing from "@/pages/Pricing";
import SuperStore from "@/pages/SuperStore";
import AIAgents from "@/pages/AIAgents";
import WellnessJourney from "@/pages/WellnessJourney";
import FeatureGate from "@/pages/FeatureGate";
import AboutFounder from "@/pages/AboutFounder";
import Glossary from "@/pages/Glossary";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminSecurity from "@/pages/admin/AdminSecurity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await apiClient.post('/auth/login', { email: email.trim().toLowerCase(), password });
    const data = response.data;
    
    // If 2FA is required, return the pre-2FA state
    if (data.requires_2fa) {
      return { requires_2fa: true, pre_2fa_token: data.pre_2fa_token, user: data.user };
    }
    
    const { token, user: userData } = data;
    if (token) {
      localStorage.setItem('auth_token', token);
    }
    setUser(userData);
    return userData;
  };

  const verify2FA = async (pre2faToken, totpCode) => {
    const response = await apiClient.post('/auth/verify-2fa', { pre_2fa_token: pre2faToken, totp_code: totpCode });
    const { token, user: userData } = response.data;
    if (token) {
      localStorage.setItem('auth_token', token);
    }
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await apiClient.post('/auth/register', { email: email.trim().toLowerCase(), password, name });
    const { token, user: userData } = response.data;
    if (token) {
      localStorage.setItem('auth_token', token);
    }
    setUser(userData);
    return userData;
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // silent logout error
    }
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  }, []);

  const contextValue = {
    user,
    loading,
    login,
    verify2FA,
    register,
    googleLogin,
    logout,
    updateUser,
    checkAuth
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await apiClient.post('/auth/google/session', { session_id: sessionId });
          
          if (response.data.token) {
            localStorage.setItem('auth_token', response.data.token);
          }
          if (response.data.user) {
            updateUser(response.data.user);
            navigate('/dashboard', { state: { user: response.data.user }, replace: true });
          }
        } catch (error) {
          // silent auth callback error
          navigate('/');
        }
      } else {
        navigate('/');
      }
    };

    processAuth();
  }, [location, navigate, updateUser]);

  return (
    <div className="min-h-screen bg-spa-bone flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-spa-terracotta border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-spa-text-secondary font-sans">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-spa-bone flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-spa-terracotta border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (location.state?.user) {
    return children;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Admin Protected Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const adminRoles = ["owner", "super_admin", "admin", "moderator", "support"];
  if (!user || !adminRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Premium Protected Route — shows inline gate for free users
const PremiumRoute = ({ children, feature, description }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-spa-bone flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-spa-terracotta border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  const paidTiers = ["basic", "premium", "elite", "retreat"];
  const adminRoles = ["owner", "super_admin", "admin", "moderator", "support"];
  const hasPremium = paidTiers.includes(user.subscription_tier) || adminRoles.includes(user.role);

  if (!hasPremium) {
    const DashboardLayout = require("@/components/layout/DashboardLayout").default;
    const { PremiumGate } = require("@/components/PremiumGate");
    return (
      <DashboardLayout>
        <PremiumGate feature={feature} description={description} variant="full" />
      </DashboardLayout>
    );
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/mind-spa" element={
        <ProtectedRoute>
          <MindSpa />
        </ProtectedRoute>
      } />
      <Route path="/ai-coach" element={
        <ProtectedRoute>
          <AICoach />
        </ProtectedRoute>
      } />
      <Route path="/mood" element={
        <ProtectedRoute>
          <MoodTracker />
        </ProtectedRoute>
      } />
      <Route path="/coins" element={
        <ProtectedRoute>
          <SMAHOOCoins />
        </ProtectedRoute>
      } />
      <Route path="/gift-store" element={
        <PremiumRoute feature="Gift Basket Store" description="Browse and purchase curated wellness gift baskets for yourself or loved ones.">
          <GiftBasketStore />
        </PremiumRoute>
      } />
      <Route path="/life-skills" element={
        <PremiumRoute feature="Life Skills" description="Access comprehensive life skills training modules for personal development and growth.">
          <LifeSkills />
        </PremiumRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute>
          <Community />
        </ProtectedRoute>
      } />
      <Route path="/oncall-live" element={
        <PremiumRoute feature="OnCALL+ Live ICU" description="Access 24/7 on-call wellness support and live crisis intervention services.">
          <OnCallLive />
        </PremiumRoute>
      } />
      <Route path="/streaming-basic" element={
        <ProtectedRoute>
          <StreamingBasic />
        </ProtectedRoute>
      } />
      <Route path="/streaming" element={
        <PremiumRoute feature="Streaming" description="Watch wellness videos, listen to curated Spotify playlists, and access on-demand therapeutic content.">
          <Streaming />
        </PremiumRoute>
      } />
      <Route path="/sessions" element={
        <ProtectedRoute>
          <Sessions />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <CalendarPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/book-appointment" element={<BookAppointment />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/feature-access" element={<FeatureGate />} />
      <Route path="/about" element={<AboutFounder />} />
      <Route path="/glossary" element={<Glossary />} />
      <Route path="/digest" element={
        <ProtectedRoute>
          <Digest />
        </ProtectedRoute>
      } />
      <Route path="/affiliate" element={
        <PremiumRoute feature="Affiliate Program" description="Join the SMAHOO affiliate program to earn commissions by referring new members.">
          <Affiliate />
        </PremiumRoute>
      } />
      <Route path="/wellness-mall" element={
        <PremiumRoute feature="Wellness Mall" description="Explore the SMAHOO Wellness Mall with curated products and services for your wellbeing.">
          <WellnessMall />
        </PremiumRoute>
      } />
      <Route path="/retreat" element={
        <PremiumRoute feature="Mind-SPA Retreat" description="Access the mindSET.reSET Retreat Continuum of Care program with seasonal modules.">
          <Retreat />
        </PremiumRoute>
      } />
      <Route path="/super-store" element={
        <PremiumRoute feature="Super Store" description="Shop exclusive SMAHOO merchandise, wellness products, and digital resources.">
          <SuperStore />
        </PremiumRoute>
      } />
      <Route path="/ai-agents" element={
        <ProtectedRoute>
          <AIAgents />
        </ProtectedRoute>
      } />
      <Route path="/wellness-journey" element={
        <ProtectedRoute>
          <WellnessJourney />
        </ProtectedRoute>
      } />
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/content" element={<AdminRoute><AdminContent /></AdminRoute>} />
      <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
      <Route path="/admin/announcements" element={<AdminRoute><AdminAnnouncements /></AdminRoute>} />
      <Route path="/admin/digest" element={<AdminRoute><AdminDigest /></AdminRoute>} />
      <Route path="/admin/blog" element={<AdminRoute><AdminBlog /></AdminRoute>} />
      <Route path="/admin/affiliates" element={<AdminRoute><AdminAffiliate /></AdminRoute>} />
      <Route path="/admin/mall" element={<AdminRoute><AdminMall /></AdminRoute>} />
      <Route path="/admin/retreat" element={<AdminRoute><AdminRetreat /></AdminRoute>} />
      <Route path="/admin/calendar" element={<AdminRoute><AdminCalendar /></AdminRoute>} />
      <Route path="/admin/media" element={<AdminRoute><AdminMedia /></AdminRoute>} />
      <Route path="/admin/audit-log" element={<AdminRoute><AdminAuditLog /></AdminRoute>} />
      <Route path="/admin/security" element={<AdminRoute><AdminSecurity /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
